import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import type { UseModalResult } from 'hooks/useModal';
import { ConfirmDialog } from 'components/common';
import { Typography } from '@mui/material';
import { useDeleteCreativeText } from '../api';
import { useNavigate } from 'react-router';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

interface CommonProps {
  id: number;
  name: string;
}

interface DeleteProps
  extends CommonProps,
    Pick<UseModalResult, 'open' | 'close' | 'isOpen'> {
}

const DeleteCreativeText: FC<DeleteProps> = ({ isOpen, close, id, name }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();

  const deleteFlow = useDeleteCreativeText({
    config: createCabinetMutationTracking({
      platform: 'tiktok',
      entity: 'creative_text',
      mode: 'delete',
      onSuccess: () => {
        onShowAlert(`"${name}" deleted`);
        navigate('/cabinet/tiktok/creative-texts');
        close();
      },
      onError: () => {
        setErrorMessage('Internal Error');
      },
    }),
  });

  const onConfirm = useCallback(() => {
    setErrorMessage(null);
    trackCabinetSubmitAttempt('tiktok', 'creative_text', 'delete', { id });
    deleteFlow.mutate({ id });
  }, [deleteFlow, id]);

  return (
    <>
      <ConfirmDialog
        title={`Delete creative text`}
        text={
          <>
            <Typography variant="body1">
              &quot;{name}&quot; <Typography variant={'body1'} component={'span'} color={'black'}>will be
              deleted without the possibility of recovery</Typography>
            </Typography>
            {!!errorMessage && (
              <Typography
                variant="body1"
                sx={(t) => ({ color: t.palette.error.main, pt: 2 })}
              >
                {errorMessage}
              </Typography>
            )}
          </>
        }
        confirmButton="Delete"
        cancelButton="Cancel"
        isOpen={isOpen}
        isPending={deleteFlow.isPending}
        close={() => {
          if (!deleteFlow.isPending) {
            close();
          }
        }}
        onCancel={() => {
          if (!deleteFlow.isPending) {
            close();
          }
        }}
        onConfirm={onConfirm}
        confirmColorButton={'error'}
      />
    </>
  );
};

export default DeleteCreativeText;
